import type { DifficultyLevel, PlayerProgress, PlayerStats } from "./types";
import { ACTIVITIES } from "./activities";
import { DIFFICULTY_LABELS } from "./difficulty";
import { PART_COUNT, SHOP_PARTS } from "./parts";
import { isRocketFlyable } from "@/lib/game/placements";
import { completedDifficultyCount, isPartUnlocked } from "./progress";

export interface ProgressSummary {
  partsUnlocked: number;
  partsTotal: number;
  partsPercent: number;
  starBalance: number;
  shopPartsOwned: number;
  shopPartsTotal: number;
  difficulty: DifficultyLevel;
  difficultyLabel: string;
  rocketSlotsFilled: number;
  rocketFlyReady: boolean;
  rocketsLaunched: number;
  builderVisits: number;
  activitiesMenuVisits: number;
  totalActivitySessions: number;
  totalActivityWins: number;
  totalDifficultiesEarned: number;
  totalDifficultiesPossible: number;
  firstPlayedAt: string | null;
  lastPlayedAt: string | null;
  perActivity: {
    id: string;
    title: string;
    emoji: string;
    sessions: number;
    wins: number;
    difficultiesEarned: number;
  }[];
}

export function summarizeProgress(progress: PlayerProgress): ProgressSummary {
  const stats = progress.stats;
  const perActivity = ACTIVITIES.map((activity) => ({
    id: activity.id,
    title: activity.title,
    emoji: activity.emoji,
    sessions: stats.activitySessions[activity.id] ?? 0,
    wins: stats.activityWins[activity.id] ?? 0,
    difficultiesEarned: completedDifficultyCount(progress, activity.id),
  }));

  const totalActivitySessions = perActivity.reduce((sum, a) => sum + a.sessions, 0);
  const totalActivityWins = perActivity.reduce((sum, a) => sum + a.wins, 0);
  const totalDifficultiesEarned = perActivity.reduce(
    (sum, a) => sum + a.difficultiesEarned,
    0,
  );

  const partsPlaced = progress.builtRocket.length;
  const flyReady = isRocketFlyable(progress.builtRocket);

  return {
    partsUnlocked: progress.unlockedPartIds.length,
    partsTotal: PART_COUNT,
    partsPercent: Math.round((progress.unlockedPartIds.length / PART_COUNT) * 100),
    starBalance: progress.starBalance,
    shopPartsOwned: SHOP_PARTS.filter((p) => isPartUnlocked(progress, p.id)).length,
    shopPartsTotal: SHOP_PARTS.length,
    difficulty: progress.difficulty,
    difficultyLabel: DIFFICULTY_LABELS[progress.difficulty],
    rocketSlotsFilled: partsPlaced,
    rocketFlyReady: flyReady,
    rocketsLaunched: stats.rocketsLaunched,
    builderVisits: stats.builderVisits,
    activitiesMenuVisits: stats.activitiesMenuVisits,
    totalActivitySessions,
    totalActivityWins,
    totalDifficultiesEarned,
    totalDifficultiesPossible: ACTIVITIES.length * 3,
    firstPlayedAt: stats.firstPlayedAt ?? null,
    lastPlayedAt: stats.lastPlayedAt ?? null,
    perActivity,
  };
}

export function formatPlayDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}
